/**
 * ScheduleStateContext.js
 * 
 * Pure state management for the schedule system.
 * This context ONLY handles state definition, actions, and reducer logic.
 * NO side effects, NO Firebase calls, NO business logic.
 * 
 * Dependencies: None (pure React state management)
 * 
 * @module ScheduleStateContext
 */

import React, { createContext, useContext, useReducer } from 'react';

// ==================== LOCAL DATE HELPER ====================

/**
 * Formats a Date to YYYY-MM-DD using LOCAL time.
 * Never use toISOString() for date strings - it returns UTC and causes
 * a one-day offset for timezones ahead of UTC (e.g. Germany CEST = UTC+2).
 */
const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// ==================== INITIAL STATE ====================

/**
 * Initial state structure for schedule management
 */
export const initialState = {
    // ==================== Data State ====================
    
    /**
     * Schedule data organized by staff ID
     * @type {Object.<string, Array<Object>>}
     * @example { "staff123": [shift1, shift2, ...] }
     */
    schedules: {},
    
    /**
     * Federal holidays
     * @type {Array<Object>}
     */
    holidays: [],
    
    /**
     * Schedule templates for quick application
     * @type {Array<Object>}
     */
    templates: [],
    
    /**
     * Currently selected staff member (single selection)
     * @type {string|null}
     */
    selectedStaff: null,
    
    /**
     * Multiple staff selection for bulk operations
     * @type {Array<string>}
     */
    selectedStaffList: [],
    
    // ==================== Time Slot Selection State ====================
    
    /**
     * Enhanced time slot selection state for calendar interactions
     * @type {Object}
     */
    timeSlotSelection: {
        isSelecting: false,              // Currently in selection mode
        isDragging: false,               // Currently dragging to select multiple slots
        selectedSlots: [],               // Array of selected slot IDs
        startSlot: null,                 // First selected slot ID
        endSlot: null,                   // Last selected slot ID
        staffId: null,                   // Staff ID for current selection
        date: null,                      // Date for current selection (YYYY-MM-DD)
        totalHours: 0,                   // Total hours in selection
        totalMinutes: 0,                 // Remaining minutes in selection
        selectionVisible: false,         // Whether selection UI is visible
        dragStartSlotId: null,           // Slot ID where drag started
        currentHoverSlotId: null         // Currently hovered slot ID
    },
    
    // ==================== View State ====================
    
    /**
     * Current date being viewed (YYYY-MM-DD)
     * Uses local time formatting to avoid UTC offset issues in timezones
     * ahead of UTC (e.g. Germany CEST = UTC+2).
     * @type {string}
     */
    currentDate: toLocalDateString(new Date()),
    
    /**
     * View mode for calendar
     * @type {'week'|'month'|'timeSlot'}
     */
    viewMode: 'month',
    
    /**
     * Date range for current view
     * @type {{start: string|null, end: string|null}}
     */
    dateRange: {
        start: null,
        end: null
    },
    
    // ==================== Schedule Calculations ====================
    
    /**
     * Schedule period objects by staff ID
     * @type {Object.<string, SchedulePeriod>}
     */
    schedulePeriods: {},
    
    /**
     * Weekly hours by staff ID
     * @type {Object.<string, number>}
     */
    weeklyHours: {},
    
    /**
     * Monthly hours by staff ID
     * @type {Object.<string, number>}
     */
    monthlyHours: {},
    
    /**
     * Adjusted weekly hours (after holiday calculations) by staff ID
     * @type {Object.<string, number>}
     */
    adjustedWeeklyHours: {},
    
    /**
     * Adjusted monthly hours (after holiday calculations) by staff ID
     * @type {Object.<string, number>}
     */
    adjustedMonthlyHours: {},
    
    /**
     * Real-time calculations for UI display
     * @type {Object}
     */
    realTimeCalculations: {
        selectedDuration: { hours: 0, minutes: 0 },
        weeklyTotal: { planned: 0, adjusted: 0 },
        monthlyTotal: { planned: 0, adjusted: 0 },
        conflictWarnings: []
    },
    
    // ==================== UI State ====================
    
    /**
     * Loading states for different operations
     * @type {Object.<string, boolean>}
     */
    loading: {
        schedules: false,
        holidays: false,
        templates: false,
        operations: false
    },
    
    /**
     * Error states for different operations
     * @type {Object.<string, string|null>}
     */
    errors: {
        schedules: null,
        holidays: null,
        templates: null,
        operations: null
    },
    
    // ==================== Real-time Subscriptions ====================
    
    /**
     * Active Firebase subscriptions (unsubscribe functions)
     * @type {Object.<string, Function>}
     */
    subscriptions: {},
    
    // ==================== Permissions ====================
    
    /**
     * Whether user can manage all staff (admin)
     * @type {boolean}
     */
    canManageAllStaff: false,
    
    /**
     * Array of staff IDs this user can manage
     * @type {Array<string>}
     */
    manageableStaffIds: [],
    
    /**
     * Department IDs the user belongs to
     * @type {Array<string>}
     */
    userDepartmentIds: [],
    
    // ==================== Optimistic Updates ====================
    
    /**
     * Pending optimistic updates (for rollback on error)
     * @type {Object.<string, any>}
     */
    optimisticUpdates: {}
};

// ==================== ACTION TYPES ====================

/**
 * All action types for the schedule reducer
 */
export const actionTypes = {
    // ==================== Data Actions ====================
    SET_SCHEDULES: 'SET_SCHEDULES',
    SET_HOLIDAYS: 'SET_HOLIDAYS',
    SET_TEMPLATES: 'SET_TEMPLATES',
    UPDATE_SCHEDULE_FOR_STAFF: 'UPDATE_SCHEDULE_FOR_STAFF',
    ADD_SHIFT: 'ADD_SHIFT',
    UPDATE_SHIFT: 'UPDATE_SHIFT',
    REMOVE_SHIFT: 'REMOVE_SHIFT',
    
    // ==================== Time Slot Selection Actions ====================
    START_TIME_SLOT_SELECTION: 'START_TIME_SLOT_SELECTION',
    UPDATE_TIME_SLOT_SELECTION: 'UPDATE_TIME_SLOT_SELECTION',
    END_TIME_SLOT_SELECTION: 'END_TIME_SLOT_SELECTION',
    CLEAR_TIME_SLOT_SELECTION: 'CLEAR_TIME_SLOT_SELECTION',
    SET_SELECTION_VISIBLE: 'SET_SELECTION_VISIBLE',
    SET_DRAG_STATE: 'SET_DRAG_STATE',
    UPDATE_HOVER_SLOT: 'UPDATE_HOVER_SLOT',
    
    // ==================== Selection Actions ====================
    SET_SELECTED_STAFF: 'SET_SELECTED_STAFF',
    SET_SELECTED_STAFF_LIST: 'SET_SELECTED_STAFF_LIST',
    ADD_TO_STAFF_SELECTION: 'ADD_TO_STAFF_SELECTION',
    REMOVE_FROM_STAFF_SELECTION: 'REMOVE_FROM_STAFF_SELECTION',
    
    // ==================== View Actions ====================
    SET_CURRENT_DATE: 'SET_CURRENT_DATE',
    SET_VIEW_MODE: 'SET_VIEW_MODE',
    SET_DATE_RANGE: 'SET_DATE_RANGE',
    
    // ==================== Calculation Actions ====================
    UPDATE_SCHEDULE_PERIOD: 'UPDATE_SCHEDULE_PERIOD',
    UPDATE_WEEKLY_HOURS: 'UPDATE_WEEKLY_HOURS',
    UPDATE_MONTHLY_HOURS: 'UPDATE_MONTHLY_HOURS',
    UPDATE_ADJUSTED_HOURS: 'UPDATE_ADJUSTED_HOURS',
    UPDATE_REAL_TIME_CALCULATIONS: 'UPDATE_REAL_TIME_CALCULATIONS',
    
    // ==================== UI Actions ====================
    SET_LOADING: 'SET_LOADING',
    SET_ERROR: 'SET_ERROR',
    CLEAR_ERROR: 'CLEAR_ERROR',
    
    // ==================== Permission Actions ====================
    SET_PERMISSIONS: 'SET_PERMISSIONS',
    
    // ==================== Subscription Actions ====================
    ADD_SUBSCRIPTION: 'ADD_SUBSCRIPTION',
    REMOVE_SUBSCRIPTION: 'REMOVE_SUBSCRIPTION',
    CLEAR_SUBSCRIPTIONS: 'CLEAR_SUBSCRIPTIONS',
    
    // ==================== Optimistic Update Actions ====================
    ADD_OPTIMISTIC_UPDATE: 'ADD_OPTIMISTIC_UPDATE',
    REMOVE_OPTIMISTIC_UPDATE: 'REMOVE_OPTIMISTIC_UPDATE',
    CLEAR_OPTIMISTIC_UPDATES: 'CLEAR_OPTIMISTIC_UPDATES'
};

// ==================== REDUCER ====================

/**
 * Schedule state reducer - handles ALL state mutations
 * 
 * @param {Object} state - Current state
 * @param {Object} action - Action object with type and payload
 * @returns {Object} New state
 */
export function scheduleReducer(state, action) {
    switch (action.type) {
        // ==================== Data Actions ====================
        
        case actionTypes.SET_SCHEDULES:
            return {
                ...state,
                schedules: action.payload
            };

        case actionTypes.SET_HOLIDAYS:
            return {
                ...state,
                holidays: action.payload
            };

        case actionTypes.SET_TEMPLATES:
            return {
                ...state,
                templates: action.payload
            };

        case actionTypes.UPDATE_SCHEDULE_FOR_STAFF:
            return {
                ...state,
                schedules: {
                    ...state.schedules,
                    [action.payload.staffId]: action.payload.shifts
                }
            };

        case actionTypes.ADD_SHIFT: {
            const { staffId, shift } = action.payload;
            return {
                ...state,
                schedules: {
                    ...state.schedules,
                    [staffId]: [
                        ...(state.schedules[staffId] || []),
                        shift
                    ]
                }
            };
        }

        case actionTypes.UPDATE_SHIFT: {
            const { staffId, shiftId, shift } = action.payload;
            const updatedShifts = (state.schedules[staffId] || []).map(s =>
                s.id === shiftId ? shift : s
            );
            return {
                ...state,
                schedules: {
                    ...state.schedules,
                    [staffId]: updatedShifts
                }
            };
        }

        case actionTypes.REMOVE_SHIFT: {
            const { staffId, shiftId } = action.payload;
            const filteredShifts = (state.schedules[staffId] || []).filter(
                shift => shift.id !== shiftId
            );
            return {
                ...state,
                schedules: {
                    ...state.schedules,
                    [staffId]: filteredShifts
                }
            };
        }

        // ==================== Time Slot Selection Actions ====================
        
        case actionTypes.START_TIME_SLOT_SELECTION: {
            const { slotId } = action.payload;
            
            // Parse slot ID to extract staffId and date
            const parts = slotId.split('-');
            if (parts.length < 5) {
                return state; // Invalid slot ID
            }
            
            const staffId = parts[0];
            const date = `${parts[1]}-${parts[2]}-${parts[3]}`;
            
            return {
                ...state,
                timeSlotSelection: {
                    isSelecting: true,
                    isDragging: false,
                    startSlot: slotId,
                    endSlot: slotId,
                    selectedSlots: [slotId],
                    staffId,
                    date,
                    totalHours: 0,
                    totalMinutes: 15, // One slot = 15 minutes
                    selectionVisible: true,
                    dragStartSlotId: slotId,
                    currentHoverSlotId: null
                }
            };
        }

        case actionTypes.UPDATE_TIME_SLOT_SELECTION: {
            const { endSlot, selectedSlots, totalMinutes } = action.payload;
            return {
                ...state,
                timeSlotSelection: {
                    ...state.timeSlotSelection,
                    endSlot,
                    selectedSlots,
                    totalHours: Math.floor(totalMinutes / 60),
                    totalMinutes: totalMinutes % 60,
                    selectionVisible: true
                }
            };
        }

        case actionTypes.END_TIME_SLOT_SELECTION:
            return {
                ...state,
                timeSlotSelection: {
                    ...state.timeSlotSelection,
                    isSelecting: false,
                    isDragging: false,
                    currentHoverSlotId: null
                }
            };

        case actionTypes.CLEAR_TIME_SLOT_SELECTION:
            return {
                ...state,
                timeSlotSelection: {
                    isSelecting: false,
                    isDragging: false,
                    selectedSlots: [],
                    startSlot: null,
                    endSlot: null,
                    staffId: null,
                    date: null,
                    totalHours: 0,
                    totalMinutes: 0,
                    selectionVisible: false,
                    dragStartSlotId: null,
                    currentHoverSlotId: null
                }
            };

        case actionTypes.SET_SELECTION_VISIBLE:
            return {
                ...state,
                timeSlotSelection: {
                    ...state.timeSlotSelection,
                    selectionVisible: action.payload
                }
            };

        case actionTypes.SET_DRAG_STATE:
            return {
                ...state,
                timeSlotSelection: {
                    ...state.timeSlotSelection,
                    isDragging: action.payload
                }
            };

        case actionTypes.UPDATE_HOVER_SLOT:
            return {
                ...state,
                timeSlotSelection: {
                    ...state.timeSlotSelection,
                    currentHoverSlotId: action.payload
                }
            };

        // ==================== Selection Actions ====================
        
        case actionTypes.SET_SELECTED_STAFF:
            return {
                ...state,
                selectedStaff: action.payload
            };

        case actionTypes.SET_SELECTED_STAFF_LIST:
            return {
                ...state,
                selectedStaffList: action.payload
            };

        case actionTypes.ADD_TO_STAFF_SELECTION:
            if (!state.selectedStaffList.includes(action.payload)) {
                return {
                    ...state,
                    selectedStaffList: [...state.selectedStaffList, action.payload]
                };
            }
            return state;

        case actionTypes.REMOVE_FROM_STAFF_SELECTION:
            return {
                ...state,
                selectedStaffList: state.selectedStaffList.filter(id => id !== action.payload)
            };

        // ==================== View Actions ====================
        
        case actionTypes.SET_CURRENT_DATE:
            return {
                ...state,
                currentDate: action.payload
            };

        case actionTypes.SET_VIEW_MODE:
            return {
                ...state,
                viewMode: action.payload
            };

        case actionTypes.SET_DATE_RANGE:
            return {
                ...state,
                dateRange: action.payload
            };

        // ==================== Calculation Actions ====================
        
        case actionTypes.UPDATE_SCHEDULE_PERIOD:
            return {
                ...state,
                schedulePeriods: {
                    ...state.schedulePeriods,
                    [action.payload.staffId]: action.payload.period
                }
            };

        case actionTypes.UPDATE_WEEKLY_HOURS:
            return {
                ...state,
                weeklyHours: {
                    ...state.weeklyHours,
                    [action.payload.staffId]: action.payload.hours
                }
            };

        case actionTypes.UPDATE_MONTHLY_HOURS:
            return {
                ...state,
                monthlyHours: {
                    ...state.monthlyHours,
                    [action.payload.staffId]: action.payload.hours
                }
            };

        case actionTypes.UPDATE_ADJUSTED_HOURS:
            return {
                ...state,
                adjustedWeeklyHours: {
                    ...state.adjustedWeeklyHours,
                    [action.payload.staffId]: action.payload.weeklyAdjusted
                },
                adjustedMonthlyHours: {
                    ...state.adjustedMonthlyHours,
                    [action.payload.staffId]: action.payload.monthlyAdjusted
                }
            };

        case actionTypes.UPDATE_REAL_TIME_CALCULATIONS:
            return {
                ...state,
                realTimeCalculations: {
                    ...state.realTimeCalculations,
                    ...action.payload
                }
            };

        // ==================== UI Actions ====================
        
        case actionTypes.SET_LOADING:
            return {
                ...state,
                loading: {
                    ...state.loading,
                    [action.payload.key]: action.payload.value
                }
            };

        case actionTypes.SET_ERROR:
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.payload.key]: action.payload.error
                }
            };

        case actionTypes.CLEAR_ERROR:
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.payload]: null
                }
            };

        // ==================== Permission Actions ====================
        
        case actionTypes.SET_PERMISSIONS:
            return {
                ...state,
                canManageAllStaff: action.payload.canManageAllStaff,
                manageableStaffIds: action.payload.manageableStaffIds,
                userDepartmentIds: action.payload.userDepartmentIds
            };

        // ==================== Subscription Actions ====================
        
        case actionTypes.ADD_SUBSCRIPTION:
            return {
                ...state,
                subscriptions: {
                    ...state.subscriptions,
                    [action.payload.key]: action.payload.unsubscribe
                }
            };

        case actionTypes.REMOVE_SUBSCRIPTION: {
            const { [action.payload]: removed, ...remainingSubscriptions } = state.subscriptions;
            return {
                ...state,
                subscriptions: remainingSubscriptions
            };
        }

        case actionTypes.CLEAR_SUBSCRIPTIONS:
            return {
                ...state,
                subscriptions: {}
            };

        // ==================== Optimistic Update Actions ====================
        
        case actionTypes.ADD_OPTIMISTIC_UPDATE:
            return {
                ...state,
                optimisticUpdates: {
                    ...state.optimisticUpdates,
                    [action.payload.id]: action.payload.data
                }
            };

        case actionTypes.REMOVE_OPTIMISTIC_UPDATE: {
            const { [action.payload]: removed, ...remainingUpdates } = state.optimisticUpdates;
            return {
                ...state,
                optimisticUpdates: remainingUpdates
            };
        }

        case actionTypes.CLEAR_OPTIMISTIC_UPDATES:
            return {
                ...state,
                optimisticUpdates: {}
            };

        // ==================== Default ====================
        
        default:
            return state;
    }
}

// ==================== CONTEXT CREATION ====================

/**
 * Schedule State Context
 * Provides state and dispatch to child components
 */
const ScheduleStateContext = createContext(undefined);

/**
 * Schedule State Provider Component
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element}
 */
export const ScheduleStateProvider = ({ children }) => {
    const [state, dispatch] = useReducer(scheduleReducer, initialState);

    const value = {
        state,
        dispatch
    };

    return (
        <ScheduleStateContext.Provider value={value}>
            {children}
        </ScheduleStateContext.Provider>
    );
};

// ==================== CUSTOM HOOKS ====================

/**
 * Hook to access schedule state and dispatch
 * 
 * @returns {{state: Object, dispatch: Function}}
 * @throws {Error} If used outside ScheduleStateProvider
 * 
 * @example
 * const { state, dispatch } = useScheduleState();
 * console.log(state.schedules);
 * dispatch({ type: actionTypes.SET_CURRENT_DATE, payload: "2024-01-15" });
 */
export const useScheduleState = () => {
    const context = useContext(ScheduleStateContext);
    
    if (context === undefined) {
        throw new Error('useScheduleState must be used within a ScheduleStateProvider');
    }
    
    return context;
};

/**
 * Hook to access only dispatch function (optimization for components that don't need state)
 * 
 * @returns {Function} Dispatch function
 */
export const useScheduleDispatch = () => {
    const { dispatch } = useScheduleState();
    return dispatch;
};

/**
 * Hook to access only state (optimization for components that don't dispatch)
 * 
 * @returns {Object} Current state
 */
export const useScheduleStateValue = () => {
    const { state } = useScheduleState();
    return state;
};

// ==================== SELECTOR HOOKS (OPTIONAL OPTIMIZATIONS) ====================

/**
 * Hook to select specific staff's schedule
 * 
 * @param {string} staffId - Staff ID
 * @returns {Array<Object>} Array of shifts for the staff
 */
export const useStaffSchedule = (staffId) => {
    const { state } = useScheduleState();
    return state.schedules[staffId] || [];
};

/**
 * Hook to get loading state for specific operation
 * 
 * @param {string} key - Loading key ('schedules', 'holidays', 'templates', 'operations')
 * @returns {boolean} Loading state
 */
export const useScheduleLoading = (key) => {
    const { state } = useScheduleState();
    return state.loading[key] || false;
};

/**
 * Hook to get error state for specific operation
 * 
 * @param {string} key - Error key ('schedules', 'holidays', 'templates', 'operations')
 * @returns {string|null} Error message or null
 */
export const useScheduleError = (key) => {
    const { state } = useScheduleState();
    return state.errors[key] || null;
};

/**
 * Hook to get current time slot selection
 * 
 * @returns {Object} Time slot selection state
 */
export const useTimeSlotSelection = () => {
    const { state } = useScheduleState();
    return state.timeSlotSelection;
};

/**
 * Hook to get current view information
 * 
 * @returns {{currentDate: string, viewMode: string, dateRange: Object}} View state
 */
export const useScheduleView = () => {
    const { state } = useScheduleState();
    return {
        currentDate: state.currentDate,
        viewMode: state.viewMode,
        dateRange: state.dateRange
    };
};

// ==================== EXPORTS ====================

export default ScheduleStateContext;